import { memo, useCallback, useMemo } from 'react';

import { merge } from '@/components';
import { Separator, Text } from '@/components/core';
import { CheckmarkFilledIcon, PendingReviewIcon, StarIcon } from '@/components/icons';
import { STATUS_TO_NAME } from '@/constants';
import { StringColumnFilter, useInboxStore } from '@/store';
import { DivProps } from '@/utils';

type TabProps = DivProps & {
  isSelected?: boolean;
  onPress?: () => void;
};

const Tab = ({ isSelected, onPress, children, ...rest }: TabProps) => {
  return (
    <div
      className={merge(
        'gap-sm group/status-tab flex cursor-pointer items-center px-3 py-1',
        isSelected ? 'rounded-md bg-white shadow-sm' : 'bg-gray-100'
      )}
      onClick={onPress}
      {...rest}
    >
      {children}
    </div>
  );
};

export type StatusTabsProps = object;

// eslint-disable-next-line no-empty-pattern
const IStatusTabs = ({}: StatusTabsProps = {}) => {
  const addFilter = useInboxStore((state) => state.addFilter);
  const updateFilter = useInboxStore((state) => state.updateFilter);
  const deleteFilter = useInboxStore((state) => state.deleteFilter);
  const filters = useInboxStore((state) => state.filters);

  const statusFilter = useMemo(
    () => filters.find((f) => f.id === 'status') as StringColumnFilter | undefined,
    [filters]
  );

  const selectedTab = useMemo(() => {
    if (!statusFilter || statusFilter.value?.length !== 1) return 'all';
    else if (statusFilter.value[0].id === 'ready-for-review') return 'review';
    else if (statusFilter.value[0].id === 'completed') return 'completed';
    else return 'all';
  }, [statusFilter]);

  const updateStatusFilter = useCallback(
    (value: string[] | null) => {
      if (value === null) {
        deleteFilter('status');
        return;
      }

      const newValues = value.map((v) => ({ id: v, name: STATUS_TO_NAME[v] }));
      if (statusFilter) {
        updateFilter('status', { value: newValues });
      } else {
        addFilter({ id: 'status', type: 'string', condition: 'is', value: newValues });
      }
    },
    [addFilter, deleteFilter, statusFilter, updateFilter]
  );

  return (
    <div className="flex gap-0.5 rounded-lg border border-gray-200 bg-gray-100 p-0.5">
      <Tab
        isSelected={selectedTab === 'all'}
        onPress={selectedTab !== 'all' ? () => updateStatusFilter(null) : undefined}
      >
        <StarIcon className="size-3" />
        <Text
          className={merge(
            'font-semibold group-hover/status-tab:text-gray-700',
            selectedTab === 'all' ? 'text-gray-700' : 'text-gray-500'
          )}
        >
          All runs
        </Text>
      </Tab>

      {!['all', 'review'].includes(selectedTab) ? <Separator orientation="vertical" className="my-1" /> : null}

      <Tab isSelected={selectedTab === 'review'} onPress={() => updateStatusFilter(['ready-for-review'])}>
        <PendingReviewIcon className="size-3" variant="warning" />
        <Text
          className={merge(
            'font-semibold group-hover/status-tab:text-gray-700',
            selectedTab === 'review' ? 'text-gray-700' : 'text-gray-500'
          )}
        >
          Needs review
        </Text>
      </Tab>

      {!['review', 'completed'].includes(selectedTab) ? <Separator orientation="vertical" className="my-1" /> : null}

      <Tab isSelected={selectedTab === 'completed'} onPress={() => updateStatusFilter(['completed'])}>
        <CheckmarkFilledIcon className="size-3" variant="success" />
        <Text
          className={merge(
            'font-semibold group-hover/status-tab:text-gray-700',
            selectedTab === 'completed' ? 'text-gray-700' : 'text-gray-500'
          )}
        >
          Completed
        </Text>
      </Tab>
    </div>
  );
};

export const StatusTabs = memo(IStatusTabs) as typeof IStatusTabs;
